<template>
  <div class="cancel-subscription-section">
    <div class="middle-container col-xs-12">
      <h4 class="text-h4 mb-6 font-bold" id="cancel-subscription-section" v-html="headline" />

      <p class="cancel-subscription-section__text" v-html="description" />

      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div
          v-for="subscription in subscriptions"
          :key="subscription.sku"
          class="rounded-lg bg-pale-mint-grey p-6 flex flex-col"
        >
          <h5 class="text-h5 text-midnight font-bold">{{ subscription.title }}</h5>

          <p class="text-p1" v-html="endDate(subscription.renewal_date)" />

          <a
            class="font-bold text-raspberry underline hover:text-raspberry2"
            :href="cancellationSurveyPath(subscription.sku)"
            @click="onCancelClick($event, subscription.sku)"
          >
            {{ buttonCopy }}
          </a>

          <p class="text-p2 text-midnight mt-4">
            {{ $t("supportText") }}
            <a href="mailto:support@blinkist.com">
              {{ $t("supportLink") }}
            </a>
          </p>
        </div>
      </div>
    </div>

    <before-you-go-modal
      :show="showModal"
      :membership-months="membershipMonths"
      :is-trialing="isTrialing"
      :trial-charge-date="trialChargeDate"
      @keep="onKeep"
      @continue="onContinue"
      @close="onModalClose"
      @video-played="onVideoPlayed"
      @contact-support="onContactSupport"
    />
  </div>
</template>

<script>
import posthog from "posthog-js"
import getLocale from "@/utils/locale"
import globals from "@/utils/globals"
import { openSupport } from "@/utils/open-support"
import BeforeYouGoModal from "@/features/settings/components/before-you-go-modal"

const SURFACE = "legacy"

export default {
  name: "CancelSubscription",
  components: {
    BeforeYouGoModal
  },
  props: {
    cancelType: {
      type: String,
      default: null
    },
    isTrialing: {
      type: Boolean,
      default: false
    },
    membershipMonths: {
      type: Number,
      default: 0
    },
    trialChargeDate: {
      type: String,
      default: ""
    },
    subscriptions: {
      type: Array,
      default: () => [],
      validator: (value) => {
        return value.every((subscription) => {
          return (
            typeof subscription === "object" &&
            typeof subscription.title === "string" &&
            typeof subscription.sku === "string" &&
            typeof subscription.renewal_date === "string"
          )
        })
      }
    }
  },
  data() {
    return {
      showModal: false,
      pendingSku: null,
      outcomeRecorded: false
    }
  },
  computed: {
    headline() {
      return this.isLeaveMupCase ? this.$t("headlineMup") : this.$t("headline")
    },
    isLeaveMupCase() {
      return this.cancelType === "leave_mup"
    },
    buttonCopy() {
      return this.isLeaveMupCase ? this.$t("leaveMup") : this.$t("cancel")
    },
    description() {
      if (this.isLeaveMupCase) {
        return this.$t("descriptionMup", { totalBooks: globals.totalBooks })
      } else if (this.isTrialing) {
        return this.$t("descriptionTrial")
      } else {
        return this.$t("descriptionPremium")
      }
    }
  },
  methods: {
    cancellationSurveyPath(sku) {
      if (sku) {
        return `/${getLocale()}/nc/show_cancel_subscription_survey?sku=${sku}`
      } else {
        return `/${getLocale()}/nc/show_cancel_subscription_survey`
      }
    },
    headlineSubscription(title) {
      return this.isLeaveMupCase ? this.$t("headlineMup") : this.$t("headlineSubscription", { title })
    },
    endDate(date) {
      if (this.isTrialing) {
        return this.$t("endDateTrial", { date })
      } else {
        return this.$t("endDatePremium", { date })
      }
    },
    onCancelClick(event, sku) {
      // intercept the cancel link to show the retention modal; the plain <a href> stays as the no-JS fallback
      event.preventDefault()
      this.pendingSku = sku
      this.outcomeRecorded = false
      this.showModal = true
      posthog.capture("cancel_before_you_go_shown", { sku, surface: SURFACE })
    },
    onKeep() {
      this.recordOutcome("kept")
      this.showModal = false
    },
    onContinue() {
      this.recordOutcome("continued")
      window.location.assign(this.cancellationSurveyPath(this.pendingSku))
    },
    onModalClose() {
      // fired by the close button, backdrop or escape key
      this.recordOutcome("dismissed")
      this.showModal = false
    },
    onVideoPlayed() {
      posthog.capture("cancel_before_you_go_video_played", { sku: this.pendingSku, surface: SURFACE })
    },
    onContactSupport() {
      this.recordOutcome("support")
      this.showModal = false
      openSupport()
    },
    recordOutcome(outcome) {
      if (this.outcomeRecorded) return
      this.outcomeRecorded = true
      posthog.capture("cancel_before_you_go_resolved", { outcome, sku: this.pendingSku, surface: SURFACE })
    }
  },
  i18n: {
    messages: {
      en: {
        headline: "Cancel your subscription",
        headlineSubscription: "Cancel {title}",
        headlineMup: "Leave shared plan",
        descriptionPremium:
          "We're sorry to see you go. You can stop future payments here.<br /><br />Once your current subscription cycle ends, you will no longer be able to read and/or listen to your saved Blinks or view Blinkist Infographics. You will only be able to access the Blink of the Day.",
        descriptionTrial:
          "We're sorry to see you go. You can stop future payments here.<br /><br />Once your current Premium trial ends, you will no longer be able to read and/or listen to your saved Blinks or view Blinkist Infographics. You will only be able to access the Blink of the Day.",
        descriptionMup:
          "Leaving the shared plan will:<br/> <ul><li>- Prevent you from reading and/ or listening to your saved Blinks</li><li></li>- Allow the owner of the shared plan to invite someone else instead</li></ul><br/>Leaving the shared plan will NOT:<br/><ul><li>- Prevent you from accessing the Blink of the Day</li></ul>",
        endDatePremium:
          "Unless you cancel the auto-renewal of your subscription, your next payment will be charged on <mark>{date}</mark>",
        endDateTrial:
          "You will be charged the first fee on <mark>{date}</mark> when your free trial ends. You can cancel anytime up until 1 day before your trial ends and you won’t be charged.",
        cancel: "Cancel auto-renewal of subscription",
        leaveMup: "Leave Shared Premium Plan",
        supportText: "Need to cancel your current subscription immediately?",
        supportLink: "Contact our support team"
      },
      de: {
        headline: "Abo verlassen",
        headlineSubscription: "{title} verlassen",
        headlineMup: "Geteiltes Abo verlassen",
        descriptionPremium:
          "Wir hoffen, du hattest eine tolle Zeit mit Blinkist Premium. Dein Abo kannst du hier kündigen.<br /><br />Sobald dein Abo abgelaufen ist, endet der unbegrenzte Zugang zu Blinkist, inklusive deiner gespeicherten Titel und Infografiken. Du kannst aber weiterhin auf den Blink des Tages zugreifen.",
        descriptionTrial:
          "Wir hoffen, du hattest eine tolle Zeit mit Blinkist Premium. Dein Abo kannst du hier kündigen.<br /><br />Sobald dein Abo abgelaufen ist, endet der unbegrenzte Zugang zu Blinkist, inklusive deiner gespeicherten Titel und Infografiken.",
        descriptionMup:
          "Verlässt du das geteilte Abo,<br/> <ul><li>- Kannst du nicht mehr auf deine gespeicherten Blinks zugreifen</li><li>- Kann der Besitzer des Abos jemand anderen einladen</li></ul><br/>Verlässt du das geteilte Abo, wird NICHT<br/><ul><li>- dein Zugriff auf den Blink des Tages eingeschränkt</li></ul>",
        endDatePremium: "Wenn du dein Abo nicht kündigst, wird deine nächste Zahlung am <mark>{date}</mark> abgebucht",
        endDateTrial:
          "Du kannst aber weiterhin auf den Blink des Tages zugreifen.<br /><br />Frühestmögliches Abo-Ende: <mark>{date}</mark>",
        cancel: "Automatische Verlängerung des Abos kündigen",
        leaveMup: "Geteiltes Abo verlassen",
        supportText: "Musst du dein aktuelles Abo sofort kündigen?",
        supportLink: "Kontaktiere unser Support-Team"
      },
      es: {
        headline: "Cancelar tu suscripción",
        headlineSubscription: "Cancelar {title}",
        headlineMup: "Salir del plan compartido",
        descriptionPremium:
          "Lamentamos que te vayas. Puedes detener futuros pagos aquí.<br /><br />Una vez que termine tu ciclo de suscripción actual, ya no podrás leer ni escuchar tus Blinks guardados o ver las Infografías de Blinkist. Solo podrás acceder al Blink del Día.",
        descriptionTrial:
          "Lamentamos que te vayas. Puedes detener futuros pagos aquí.<br /><br />Una vez que termine tu prueba gratuita de Premium, ya no podrás leer ni escuchar tus Blinks guardados o ver las Infografías de Blinkist. Solo podrás acceder al Blink del Día.",
        descriptionMup:
          "Salir del plan compartido:<br/> <ul><li>- Te impedirá leer y/o escuchar tus Blinks guardados</li><li>- Permitirá al propietario del plan compartido invitar a otra persona en tu lugar</li></ul><br/>Salir del plan compartido NO:<br/><ul><li>- Te impedirá acceder al Blink del Día</li></ul>",
        endDatePremium:
          "A menos que canceles la renovación automática de tu suscripción, tu próximo pago se cobrará el <mark>{date}</mark>",
        endDateTrial:
          "Se te cobrará la primera cuota el <mark>{date}</mark> cuando termine tu prueba gratuita. Puedes cancelar en cualquier momento hasta 1 día antes de que termine tu prueba y no se te cobrará nada.",
        cancel: "Cancelar renovación automática de suscripción",
        leaveMup: "Salir del Plan Premium Compartido",
        supportText: "¿Necesitas cancelar tu suscripción actual inmediatamente?",
        supportLink: "Contacta a nuestro equipo de soporte"
      },
      pt: {
        headline: "Cancelar sua assinatura",
        headlineSubscription: "Cancelar {title}",
        headlineMup: "Sair do plano compartilhado",
        descriptionPremium:
          "É uma pena ver você partir. Você pode interromper futuros pagamentos aqui.<br /><br />Quando seu ciclo de assinatura atual terminar, você não conseguirá mais ler nem ouvir seus Blinks salvos ou visualizar as Infografias da Blinkist. Você só conseguirá acessar o Blink do Dia.",
        descriptionTrial:
          "É uma pena ver você partir. Você pode interromper futuros pagamentos aqui.<br /><br />Quando seu teste grátis do Premium terminar, você não conseguirá mais ler nem ouvir seus Blinks salvos ou visualizar as Infografias da Blinkist. Você só conseguirá acessar o Blink do Dia.",
        descriptionMup:
          "Sair do plano compartilhado vai:<br/> <ul><li>- Impedir que você leia e/ou ouça seus Blinks salvos</li><li>- Permitir que o proprietário do plano compartilhado convide outra pessoa no seu lugar</li></ul><br/>Sair do plano compartilhado NÃO vai:<br/><ul><li>- Impedir que você acesse o Blink do Dia</li></ul>",
        endDatePremium:
          "A menos que você cancele a renovação automática da sua assinatura, seu próximo pagamento será cobrado em <mark>{date}</mark>",
        endDateTrial:
          "Você será cobrado pela primeira vez em <mark>{date}</mark> quando seu teste grátis terminar. Você pode cancelar a qualquer momento até 1 dia antes do fim do seu teste e não será cobrado.",
        cancel: "Cancelar renovação automática da assinatura",
        leaveMup: "Sair do Plano Premium Compartilhado",
        supportText: "Precisa cancelar sua assinatura atual imediatamente?",
        supportLink: "Entre em contato com nossa equipe de suporte"
      }
    }
  }
}
</script>
